import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/Layout";
import { RolesManager } from "@/components/RolesManager";
import { initials, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, RefreshCw } from "lucide-react";
import { useTableSort, SortTh, useColumns, ColumnPicker, ExportCsvButton } from "@/components/SortableHeader";

export default function Admins() {
  const { user: me } = useAuth();
  const { t } = useLang();
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]); // [{id, name}]
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user" });
  const [editing, setEditing] = useState(null); // the user row being edited
  const [editForm, setEditForm] = useState({ name: "", email: "", password: "" });

  const loadRoles = () => api.get("/roles").then((r) => setRoles([...r.data.builtin, ...r.data.custom].map((x) => ({ id: x.id, name: x.name }))));
  const load = () => api.get("/admins").then((r) => setAdmins(r.data));
  useEffect(() => { load(); loadRoles(); }, []);

  const roleName = (id) => (roles.find((r) => r.id === id) || {}).name || id;

  const sort = useTableSort("name", "asc", "admins");
  const cols = useColumns("admins");
  const sortedAdmins = useMemo(() => sort.sortRows(admins, {
    created: (a) => a.created_at || "",
    role: (a) => roleName(a.role),
  }), [admins, roles, sort]);
  const csvHeaders = [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    ...(cols.show("created") ? [{ key: "created", label: "Created" }] : []),
    { key: "role", label: "Role" },
  ];
  const csvRows = sortedAdmins.map((a) => ({ name: a.name, email: a.email, created: a.created_at || "", role: roleName(a.role) }));

  const create = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("All fields required"); return; }
    try { await api.post("/admins", form); toast.success("Console user created"); setOpen(false); setForm({ name: "", email: "", password: "", role: "user" }); await load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const changeRole = async (id, role) => { try { await api.patch(`/admins/${id}`, { role }); toast.success("Role updated"); await load(); } catch (e) { toast.error(apiErr(e)); } };
  const del = async (id) => { try { await api.delete(`/admins/${id}`); toast.success("User removed"); await load(); } catch (e) { toast.error(apiErr(e)); } };

  const openEdit = (a) => { setEditing(a); setEditForm({ name: a.name || "", email: a.email || "", password: "" }); };
  const genPassword = async () => {
    try { const { data } = await api.post("/utils/generate-password"); setEditForm((f) => ({ ...f, password: data.password })); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveEdit = async () => {
    if (!editing) return;
    const payload = {};
    if (editForm.name && editForm.name !== editing.name) payload.name = editForm.name;
    if (!editing.external && editForm.email && editForm.email.toLowerCase() !== (editing.email || "").toLowerCase()) payload.email = editForm.email;
    if (editForm.password) payload.password = editForm.password;
    if (!Object.keys(payload).length) { setEditing(null); return; }
    try {
      await api.patch(`/admins/${editing.id}`, payload);
      toast.success(editForm.password ? "User updated — password reset" : "User updated");
      setEditing(null);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <>
      <PageHeader title="Admins & Roles" subtitle="Who can access this console and what they can do" />
      <div className="p-6 md:p-10">
        <Tabs defaultValue="users">
          <TabsList className="mb-6 rounded-sm">
            <TabsTrigger value="users" data-testid="tab-users" className="rounded-sm">Console users</TabsTrigger>
            <TabsTrigger value="roles" data-testid="tab-roles" className="rounded-sm">Roles & permissions</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <div className="mb-4 flex items-center justify-end gap-2">
              <ColumnPicker label={t("cols.columns")} cols={cols} columns={[{ key: "created", label: "Created" }]} />
              <ExportCsvButton label={t("cols.export")} filename="console-users.csv" headers={csvHeaders} rows={csvRows} />
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="new-admin" className="rounded-sm bg-primary hover:bg-[#002480]"><Plus size={16} className="mr-2" /> Add user</Button>
                </DialogTrigger>
                <DialogContent className="rounded-sm sm:max-w-md">
                  <DialogHeader><DialogTitle className="font-display uppercase tracking-tight">New console user</DialogTitle>
                    <DialogDescription>Create a local console account and assign it a role.</DialogDescription></DialogHeader>
                  <div className="space-y-4 py-2">
                    <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Name</Label>
                      <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-name" className="rounded-sm border-zinc-300" /></div>
                    <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Email</Label>
                      <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-email" className="rounded-sm border-zinc-300" /></div>
                    <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Temporary password</Label>
                      <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-password" className="rounded-sm border-zinc-300 font-mono text-sm" /></div>
                    <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Role</Label>
                      <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                        <SelectTrigger className="rounded-sm border-zinc-300" data-testid="admin-role"><SelectValue /></SelectTrigger>
                        <SelectContent>{roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                    <Button onClick={create} data-testid="create-admin" className="rounded-sm bg-primary hover:bg-[#002480]">Create</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="border border-zinc-200 bg-white">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-zinc-200 bg-zinc-50 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                    <SortTh label="User" sortKey="name" sort={sort} />
                    {cols.show("created") && <SortTh label="Created" sortKey="created" sort={sort} className="hidden md:table-cell" />}
                    <SortTh label="Role" sortKey="role" sort={sort} />
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAdmins.map((a) => (
                    <tr key={a.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`admin-row-${a.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 items-center justify-center bg-zinc-900 text-xs font-bold text-white">{initials(a.name)}</span>
                          <div><div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-zinc-900">{a.name}
                            {a.id === me.id && <span className="text-[10px] uppercase text-primary">you</span>}
                            {a.external && <span className="border border-violet-200 bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-violet-700" data-testid={`external-badge-${a.id}`}>{t("admins.external")}</span>}
                            {a.pendingAccess && <span className="border border-amber-200 bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-700" data-testid={`pending-badge-${a.id}`}>Pending access</span>}
                            {a.e2eTestUser && <span className="border border-sky-200 bg-sky-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-sky-700" data-testid={`e2e-badge-${a.id}`} title={t("admins.testUserHint")}>{t("admins.testUser")}</span>}
                          </div>
                            <div className="font-mono text-xs text-zinc-400">{a.email}</div></div>
                        </div>
                      </td>
                      {cols.show("created") && <td className="hidden px-4 py-3 text-sm text-zinc-500 md:table-cell">{fmtDate(a.created_at)}</td>}
                      <td className="px-4 py-3">
                        <Select value={a.role} onValueChange={(v) => changeRole(a.id, v)} disabled={a.id === me.id || a.e2eTestUser}>
                          <SelectTrigger className="h-8 w-40 rounded-sm border-zinc-300 text-xs" data-testid={`role-select-${a.id}`}><SelectValue>{roleName(a.role)}</SelectValue></SelectTrigger>
                          <SelectContent>{roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {a.e2eTestUser ? (
                            <span className="text-[10px] uppercase tracking-wider text-zinc-400" data-testid={`e2e-locked-${a.id}`}>{t("admins.locked")}</span>
                          ) : (
                            <>
                              <button onClick={() => openEdit(a)} data-testid={`edit-admin-${a.id}`} className="p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700" title="Edit user"><Pencil size={15} /></button>
                              {a.id !== me.id && (
                                <button onClick={() => del(a.id)} data-testid={`delete-admin-${a.id}`} className="p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600" title="Remove user"><Trash2 size={15} /></button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="roles">
            <RolesManager onRolesChanged={loadRoles} />
          </TabsContent>
        </Tabs>

        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="rounded-sm sm:max-w-md" data-testid="edit-admin-dialog">
            <DialogHeader>
              <DialogTitle className="font-display uppercase tracking-tight">Edit console user</DialogTitle>
              <DialogDescription>Update the name{editing?.external ? " (email & password are managed in Entra ID)." : ", email, or reset the sign-in password."}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Name</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  data-testid="edit-admin-name" className="rounded-sm border-zinc-300" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Email</Label>
                <Input value={editing?.external ? (editing?.email || "") : editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  disabled={editing?.external} type="email" data-testid="edit-admin-email"
                  className={editing?.external ? "rounded-sm border-zinc-200 bg-zinc-50 font-mono text-sm" : "rounded-sm border-zinc-300 font-mono text-sm"} />
              </div>
              {editing?.external ? (
                <div className="rounded-sm border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-700">
                  This account signs in with Microsoft (Entra ID) and has no local password to reset.
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Reset password (optional)</Label>
                  <div className="flex items-center gap-2">
                    <Input value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                      data-testid="edit-admin-password" placeholder="Leave blank to keep current"
                      className="rounded-sm border-zinc-300 font-mono text-sm" />
                    <Button type="button" variant="outline" onClick={genPassword} data-testid="edit-admin-genpw"
                      className="shrink-0 rounded-sm border-zinc-300" title="Generate password">
                      <RefreshCw size={15} />
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEditing(null)} className="rounded-sm">Cancel</Button>
              <Button onClick={saveEdit} data-testid="save-admin-edit" className="rounded-sm bg-primary hover:bg-[#002480]">Save changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
